import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, Checkbox } from "@mui/material";
import { Autocomplete, Button, IconButton, TextField } from "@totalsoft/rocket-ui";
import { DateTime } from "@ts-private/rocket-extensions";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import Autosuggest from "components/common/inputs/Autosuggest";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { useMemo } from "react";

export const PFPartnerComponent = ({
  partner,
  citizenships,
  genderTypes,
  personDocumentTypes,
  maritalStatuses,
  specialRelations,
  onSave,
  onPropertyChange,
  onPartnerPropertyChange,
  loadPersons,
  saveDisabled,
  validationPartner,
  uploadClick,
  fileInputRef,
  onFileSelected,
  hasWritePermission
}) => {
  const { t } = useTranslation();

  const spouseValue = useMemo(() => {
    if (!partner.individual || !partner.individual.lifePartnerId) return null;
    return { id: partner.individual.lifePartnerId, partnerName: partner.individual.lifePartnerName };
  }, [partner]);

  return (
    <>
      <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={onFileSelected} />
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <TextField
          required
          fullWidth
          id="pfPartner_firstName"
          value={partner.individual ? partner.individual.firstName || "" : ""}
          name="firstName"
          label={t("Partner.FirstName")}
          error={!isValid(validationPartner.firstName)}
          helperText={getErrors(validationPartner.firstName)}
          onChange={onPropertyChange("firstName")}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <TextField
          required
          fullWidth
          id="pfPartner_lastName"
          value={partner.individual ? partner.individual.lastName || "" : ""}
          name="lastName"
          label={t("Partner.LastName")}
          error={!isValid(validationPartner.lastName)}
          helperText={getErrors(validationPartner.lastName)}
          onChange={onPropertyChange("lastName")}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <TextField
          required
          fullWidth
          id="pfPartner_uniqueID"
          value={partner.uniqueId || ""}
          name="uniqueId"
          label={t("Partner.PersonUniqueID")}
          error={!isValid(validationPartner.uniqueId)}
          helperText={getErrors(validationPartner.uniqueId)}
          onChange={onPartnerPropertyChange("uniqueId")}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_personDocumentType"
          label={t("Partner.PersonDocumentTypeId")}
          value={
            personDocumentTypes.length > 0
              ? partner.individual
                ? partner.individual.personDocumentTypeId || ""
                : ""
              : ""
          }
          valueKey="personDocumentTypeId"
          labelKey="personDocumentTypeName"
          simpleValue={true}
          onChange={onPropertyChange("personDocumentTypeId")}
          disabled={!hasWritePermission}
          options={personDocumentTypes}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <TextField
          fullWidth
          id="pfPartner_idSeriesAndNumber"
          value={partner.individual ? partner.individual.idSeriesAndNumber || "" : ""}
          name="idSeriesAndNumber"
          label={t("Partner.SeriesAndNumber")}
          onChange={onPropertyChange("idSeriesAndNumber")}
          disabled={!hasWritePermission}
          error={!isValid(validationPartner.idSeriesAndNumber)}
          helperText={getErrors(validationPartner.idSeriesAndNumber)}
          endAdornment={
            <IconButton
              id={"pfPartner_ocrBtn"}
              onClick={uploadClick}
              variant="text"
              color="primary"
              size="tiny"
              disabled={!hasWritePermission}
              tooltip={t("Partner.OCR")}
            >
              <PhotoCameraIcon />
            </IconButton>
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <TextField
          fullWidth
          id="pfPartner_idIssuedBy"
          value={partner.individual ? partner.individual.idIssuedBy || "" : ""}
          name="idIssuedBy"
          label={t("Partner.IssuedBy")}
          onChange={onPropertyChange("idIssuedBy")}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <DateTime
          fullWidth
          id="pfPartner_identityCardReleaseDate"
          value={partner.individual?.identityCardReleaseDate}
          label={t("Partner.IdentityCardReleaseDate")}
          onChange={onPropertyChange("identityCardReleaseDate")}
          timeFormat={false}
          disabled={!hasWritePermission}
          error={!isValid(validationPartner.identityCardReleaseDate)}
          helperText={getErrors(validationPartner.identityCardReleaseDate)}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <DateTime
          fullWidth
          id="pfPartner_identityCardExpireDate"
          value={partner.individual?.identityCardExpireDate}
          label={t("Partner.IdentityCardExpireDate")}
          onChange={onPropertyChange("identityCardExpireDate")}
          timeFormat={false}
          disabled={!hasWritePermission}
          error={!isValid(validationPartner.identityCardExpireDate)}
          helperText={getErrors(validationPartner.identityCardExpireDate)}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4} />
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_citizenship"
          label={t("Partner.Citizenship")}
          value={partner.individual ? partner.individual.citizenshipId : ""}
          valueKey="citizenshipId"
          labelKey="citizenshipName"
          simpleValue={true}
          onChange={onPropertyChange("citizenshipId")}
          options={citizenships}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_nationality"
          label={t("Partner.Nationality")}
          error={!isValid(validationPartner.nationalityId)}
          helperText={getErrors(validationPartner.nationalityId)}
          value={partner.individual ? partner.individual.nationalityId : ""}
          valueKey="citizenshipId"
          labelKey="citizenshipName"
          simpleValue={true}
          onChange={onPropertyChange("nationalityId")}
          options={citizenships}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <TextField
          fullWidth
          id="pfPartner_birthPlace"
          value={partner.individual ? partner.individual.birthPlace || "" : ""}
          name="birthPlace"
          label={t("Partner.BirthPlace")}
          onChange={onPropertyChange("birthPlace")}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <DateTime
          fullWidth
          id="pfPartner_birthDate"
          value={partner.individual?.birthDate}
          label={t("Partner.BirthDate")}
          onChange={onPropertyChange("birthDate")}
          error={!isValid(validationPartner.birthDate)}
          helperText={getErrors(validationPartner.birthDate)}
          timeFormat={false}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_maritalStatus"
          label={t("Partner.MaritalStatus")}
          simpleValue={true}
          value={maritalStatuses.length > 0 ? (partner.individual ? partner.individual.maritalStatusId || "" : "") : ""}
          valueKey="maritalStatusId"
          labelKey="maritalStatusName"
          onChange={onPropertyChange("maritalStatusId")}
          options={maritalStatuses}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <div id={"pfPartner_lifePartner"}>
          <Autosuggest
            label={t("Partner.Spouse")}
            value={spouseValue}
            onChange={onPartnerPropertyChange("lifePartnerId", "lifePartnerName")}
            disabled={!hasWritePermission}
            isClearable={false}
            valueKey="id"
            labelKey="partnerName"
            loadOptions={loadPersons}
          />
        </div>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_gender"
          label={t("Partner.Sex")}
          value={partner.individual && partner.individual.genderId ? partner.individual.genderId : ""}
          error={!isValid(validationPartner.genderId)}
          helperText={getErrors(validationPartner.genderId)}
          valueKey="genderTypeId"
          labelKey="genderTypeName"
          onChange={onPropertyChange("genderId")}
          disabled={!hasWritePermission}
          options={genderTypes}
          simpleValue={true}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <span> {t("Partner.IsExternal")}</span>
        <Checkbox
          id="pfPartner_isExternal"
          checked={Boolean((partner.individual && partner.isExternal) || false)}
          onChange={onCheckBoxChange(onPartnerPropertyChange("isExternal"))}
          disabled={!hasWritePermission}
          color="primary"
          name="external"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <span> {t("Partner.PEP")}</span>
        <Checkbox
          id="pfPartner_pep"
          checked={Boolean(partner.individual?.pep || false)}
          onChange={onCheckBoxChange(onPropertyChange("pep"))}
          disabled={!hasWritePermission}
          color="primary"
          name="pep"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <Autocomplete
          fullWidth
          id="pfPartner_specialRelation"
          label={t("Partner.RelationType")}
          simpleValue={true}
          value={specialRelations.length > 0 ? (partner.individual ? partner.individual.specialRelationId || "" : "") : ""}
          valueKey="id"
          labelKey="name"
          onChange={onPropertyChange("specialRelationId")}
          options={specialRelations}
          disabled={!hasWritePermission}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          id={"pfPartner_remarks"}
          label={t("Partner.Remarks")}
          value={partner.remark || ""}
          onChange={onPartnerPropertyChange("remark")}
          disabled={!hasWritePermission}
          multiline
          inputProps={{ maxLength: 255 }}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Button
          id="pfPartner_btnSave"
          color="primary"
          variant="contained"
          onClick={onSave}
          disabled={saveDisabled || !hasWritePermission || !(validationPartner |> isValid)}
        >
          {t("Save")}
        </Button>
      </Grid>
    </>
  );
};

PFPartnerComponent.propTypes = {
  partner: PropTypes.object.isRequired,
  citizenships: PropTypes.array,
  genderTypes: PropTypes.array,
  maritalStatuses: PropTypes.array,
  specialRelations: PropTypes.array,
  personDocumentTypes: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onPartnerPropertyChange: PropTypes.func.isRequired,
  loadPersons: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  validationPartner: PropTypes.object.isRequired,
  uploadClick: PropTypes.func.isRequired,
  fileInputRef: PropTypes.object,
  onFileSelected: PropTypes.func.isRequired,
  hasWritePermission: PropTypes.bool.isRequired
};

export default PFPartnerComponent;
